/**
 * UILayout - Template-based UI component with mustache-style interpolation ({{variable}}),
 * CSS style injection, and event delegation for efficient DOM management.
 */

import { UIElement } from './UIElement.js';
import { UIResource } from './UIResource.js';

export interface UILayoutOptions {
  template: string; // HTML template string
  styles?: string; // CSS styles string
  data?: Record<string, any>; // Data for template interpolation
  eventHandlers?: UIEventHandlerMap; // Event handler delegates
}

export interface UIEventHandler {
  selector: string;
  event: string;
  handler: (element: HTMLElement, event: Event) => void;
}

export type UIEventHandlerMap = Record<string, UIEventHandler | UIEventHandler[]>;

export class UILayout extends UIResource {
  private element: UIElement;
  private styleElement: HTMLStyleElement | null = null;
  private eventHandlers: UIEventHandler[] = [];
  private eventDelegates = new Map<string, EventListener>();

  constructor(options: UILayoutOptions) {
    super();

    // Create the main container
    this.element = new UIElement({
      className: 'ui-layout'
    });

    // Process template with data interpolation
    let processedTemplate = options.template;
    if (options.data) {
      processedTemplate = this.interpolateTemplate(options.template, options.data);
    }

    this.element.setHTML(processedTemplate);

    // Add styles if provided
    if (options.styles) {
      this.addStyles(options.styles);
    }

    // Setup event handlers if provided
    if (options.eventHandlers) {
      this.setupEventHandlers(options.eventHandlers);
    }
  }

  private interpolateTemplate(template: string, data: Record<string, any>): string {
    return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return data[key] !== undefined ? String(data[key]) : match;
    });
  }

  private addStyles(styles: string): void {
    this.styleElement = document.createElement('style');
    this.styleElement.textContent = styles;
    document.head.appendChild(this.styleElement);
  }

  private setupEventHandlers(eventHandlerMap: UIEventHandlerMap): void {
    Object.entries(eventHandlerMap).forEach(([key, handlerConfig]) => {
      const handlers = Array.isArray(handlerConfig) ? handlerConfig : [handlerConfig];

      handlers.forEach(handler => {
        this.addEventHandler(handler);
      });
    });
  }

  /**
     * Add an event handler with delegation
     */
  public addEventHandler(config: UIEventHandler): void {
    const { selector, event, handler } = config;
    const delegateKey = `${event}:${selector}`;

    // Create delegated event listener
    const delegateListener: EventListener = (e: Event) => {
      const target = e.target as HTMLElement;
      const matchingElement = target.closest(selector) as HTMLElement;

      if (matchingElement && this.element.element.contains(matchingElement)) {
        try {
          handler(matchingElement, e);
        } catch (error) {
          console.error(`Error in UI event handler for ${selector}:${event}:`, error);
        }
      }
    };

    // Remove existing delegate if present
    this.removeEventHandler(selector, event);

    // Add new delegate
    this.element.element.addEventListener(event, delegateListener, true);
    this.eventDelegates.set(delegateKey, delegateListener);
    this.eventHandlers.push(config);
  }

  /**
     * Remove an event handler
     */
  public removeEventHandler(selector: string, event: string): void {
    const delegateKey = `${event}:${selector}`;
    const existingDelegate = this.eventDelegates.get(delegateKey);

    if (existingDelegate) {
      this.element.element.removeEventListener(event, existingDelegate, true);
      this.eventDelegates.delete(delegateKey);

      // Remove from handlers array
      this.eventHandlers = this.eventHandlers.filter(
        h => !(h.selector === selector && h.event === event)
      );
    }
  }

  /**
     * Add multiple event handlers at once
     */
  public addEventHandlers(handlers: UIEventHandler[]): void {
    handlers.forEach(handler => this.addEventHandler(handler));
  }

  /**
     * Convenient method to add click handlers
     */
  public onClick(selector: string, handler: (element: HTMLElement, event: MouseEvent) => void): void {
    this.addEventHandler({
      selector,
      event: 'click',
      handler: handler as (element: HTMLElement, event: Event) => void
    });
  }

  /**
     * Convenient method to add input handlers
     */
  public onInput(selector: string, handler: (element: HTMLElement, event: InputEvent) => void): void {
    this.addEventHandler({
      selector,
      event: 'input',
      handler: handler as (element: HTMLElement, event: Event) => void
    });
  }

  /**
     * Convenient method to add change handlers
     */
  public onChange(selector: string, handler: (element: HTMLElement, event: Event) => void): void {
    this.addEventHandler({
      selector,
      event: 'change',
      handler
    });
  }

  public getElement(): UIElement {
    return this.element;
  }

  public appendTo(parent: UIElement | string | HTMLElement): this {
    this.element.appendTo(parent);
    return this;
  }

  public remove(): void {
    // Clean up event delegates
    this.eventDelegates.forEach((listener, key) => {
      const [event] = key.split(':');
      this.element.element.removeEventListener(event, listener, true);
    });
    this.eventDelegates.clear();
    this.eventHandlers = [];

    if (this.styleElement) {
      this.styleElement.remove();
      this.styleElement = null;
    }
    this.element.remove();
    this.unregisterFromUIManager();
  }

  public querySelector(selector: string): HTMLElement | null {
    return this.element.querySelector(selector);
  }

  public querySelectorAll(selector: string): NodeListOf<HTMLElement> {
    return this.element.querySelectorAll(selector);
  }
}
